import {
  Component,
  ViewChild,
  ChangeDetectorRef,
  HostListener,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

import * as fit from 'fit.js';
import * as jQuery from 'jquery';
import './vendor/turn';
import { Spread } from '../interfaces/spread';

@Component({
  selector: 'album',
  styleUrls: ['./album.component.scss'],
  templateUrl: './album.component.html',
})
export class AlbumComponent implements OnChanges {
  @ViewChild('turnContainer', { static: true }) private turnContainer: ElementRef;

  @Input() currentPage: number;
  @Input() countPages: number;
  @Input() isAllImagesLoaded: boolean;
  @Input() isPriorityImagesLoaded: boolean;
  @Input() isTurnInitialized: boolean;
  @Input() spreadSize: any;
  @Input() spreads: Spread[];
  @Output() onChangePage = new EventEmitter();
  @Output() onLoadImage = new EventEmitter();
  @Output() onLoadPlaceholderImage = new EventEmitter();
  @Output() setTurnInitialized = new EventEmitter();
  @Output() setCountPages = new EventEmitter();

  private targetPage = 1;

  public isMobileWidth = window.innerWidth < 576;
  public albumSize = {
    width: 0,
    height: 0,
    renderWidth: 0,
    renderHeight: 0,
    marginTop: 0,
    marginLeft1: 0,
    marginLeft2: 0,
  };

  constructor(
    private cdRef: ChangeDetectorRef,
    private mainContainer: ElementRef
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if ('currentPage' in changes) {
      this.handleChangePage(changes['currentPage'].currentValue);
    }

    if (
      'isPriorityImagesLoaded' in changes &&
      changes['isPriorityImagesLoaded'].currentValue === true
    ) {
      this.initTurnJS();
    }
  }

  public getDepthWidth() {
    return 16 * Math.min(1, this.currentPage / this.countPages);
  }

  public firstPage() {
    if (this.isTurnInitialized) {
      jQuery(this.turnContainer.nativeElement).turn('page', 1);
    }
  }

  public lastPage() {
    if (this.isTurnInitialized) {
      jQuery(this.turnContainer.nativeElement).turn('page', this.countPages);
    }
  }

  @HostListener('document:keydown.ArrowLeft', ['$event'])
  public prevPage(event) {
    if (event) {
      event.preventDefault();
    }
    if (this.isTurnInitialized) {
      jQuery(this.turnContainer.nativeElement).turn('previous');
    }
  }

  @HostListener('document:keydown.ArrowRight', ['$event'])
  public nextPage(event) {
    if (event) {
      event.preventDefault();
    }
    if (this.isTurnInitialized) {
      jQuery(this.turnContainer.nativeElement).turn('next');
    }
  }

  @HostListener('window:resize')
  private onResize() {
    if (!this.isTurnInitialized) {
      return;
    }

    const newSize = this.getSize();
    this.albumSize = this.calculateAlbumSizes(newSize);

    this.cdRef.detectChanges();

    jQuery(this.turnContainer.nativeElement).turn(
      'size',
      newSize.width,
      newSize.height
    );
    jQuery(this.turnContainer.nativeElement).turn('resize');
  }

  private initTurnJS() {
    const el = this.turnContainer.nativeElement;
    const jQAlbum: any = jQuery(el);
    const size = this.getSize();
    this.albumSize = this.calculateAlbumSizes(size);

    this.cdRef.detectChanges();

    jQAlbum.turn({
      ...size,
      autoCenter: true,
    });
    this.setCountPages.emit(jQAlbum.turn('pages'));
    jQAlbum.on('turning', (event, page) => {
      if (page !== this.currentPage && this.isTurnInitialized) {
        this.targetPage = page;
        this.onChangePage.emit(page);
      }
    });
    jQAlbum.on('turned', (event, page) => {
      if (page !== this.currentPage && this.isTurnInitialized) {
        this.targetPage = page;
        this.onChangePage.emit(page);
      }
    });
    this.setTurnInitialized.emit(true);
    this.onResize();
  }

  private handleChangePage(page) {
    if (!this.isTurnInitialized || !this.turnContainer) {
      return;
    }

    this.cdRef.detectChanges();

    const turnJSPage = jQuery(this.turnContainer.nativeElement).turn('page');
    if (turnJSPage !== page && page !== this.targetPage) {
      this.targetPage = page;
      jQuery(this.turnContainer.nativeElement).turn('page', page);
    }
  }

  private getSize() {
    const pictureSize = this.spreadSize;

    const result = fit(
      { width: pictureSize.width, height: pictureSize.height },
      this.mainContainer.nativeElement,
      {
        apply: false,
      }
    );

    return {
      width: Math.floor(result.width),
      height: Math.floor(result.height),
    };
  }

  private calculateAlbumSizes(size) {
    const marginTop = this.convertToEven(size.height * 0.01, 1);
    const renderHeight = size.height - marginTop * 2;
    const marginLeft1 = this.convertToEven(size.width * 0.01, 1);
    const renderWidth = size.width - marginLeft1 * 2;
    const marginLeft2 = -renderWidth / 2;
    return {
      width: size.width,
      height: size.height,
      renderWidth,
      renderHeight,
      marginTop,
      marginLeft1,
      marginLeft2,
    };
  }

  private convertToEven(num, toFixed = 0) {
    num = +num.toFixed(toFixed);
    let converterStr = '1',
      converter;
    if (toFixed > 0) {
      toFixed = +toFixed.toFixed();
      for (let i = 1; i <= toFixed; i++) {
        converterStr += '0';
      }
    }
    converter = +converterStr;
    return (num * converter) % 2 === 0
      ? num
      : (num * converter + 1) / converter;
  }
}
